import { useState, useCallback } from "react";
import { IMsalContext } from "@azure/msal-react";
import { getFilterOptions } from "./filterOptionsApi";
import { PROFILE_TYPE_LABELS, EXCLUDED_FILTER_PROFILE_TYPES } from "utils/normalizeSpaces";

interface Options {
  text: string;
  value: string;
}

interface FilterOptions {
  profileTypes: Options[];
  projectTypes: Options[];
  bucketTypes: Options[];
  regions: Options[];
}

interface UseFilterOptionsReturn {
  availableProfileTypes: Options[];
  availableProjectTypes: Options[];
  availableBucketTypes: Options[];
  availableRegions: Options[];
  isLoadingFilterOptions: boolean;
  fetchFilterOptions: (domainName?: string, region?: string) => Promise<void>;
  resetFilterOptions: () => void;
}

// Default options for fallback in {text, value} format
const DEFAULT_PROJECT_TYPES: Options[] = [
  { text: "Data", value: "data" },
  { text: "Analytics", value: "analytics" },
  { text: "DSE App", value: "dse_app" },
];

const DEFAULT_PROFILE_TYPES: Options[] = Object.entries(PROFILE_TYPE_LABELS)
  .filter(([value]) => !EXCLUDED_FILTER_PROFILE_TYPES.includes(value))
  .map(([value, text]) => ({ text, value }));

// Static bucket types - always use these, don't fetch from backend
const DEFAULT_BUCKET_TYPES: Options[] = [
  { text: "GxP", value: "gxp" },
  { text: "Non-GxP", value: "nongxp" },
];

const DEFAULT_REGIONS: Options[] = [];

// Mapping for API values to display values
const PROJECT_TYPE_MAPPING: { [key: string]: string } = {
  "data": "Data",
  "analytics": "Analytics",
  "dse_app": "DSE App",
};

export const useFilterOptions = (msalInstance: IMsalContext): UseFilterOptionsReturn => {
  // Fix state types to match return type
  const [availableProjectTypes, setAvailableProjectTypes] = useState<Options[]>(DEFAULT_PROJECT_TYPES);
  const [availableBucketTypes, setAvailableBucketTypes] = useState<Options[]>(DEFAULT_BUCKET_TYPES);
  const [availableProfileTypes, setAvailableProfileTypes] = useState<Options[]>(DEFAULT_PROFILE_TYPES);
  const [availableRegions, setAvailableRegions] = useState<Options[]>(DEFAULT_REGIONS);
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);

  // Map API values to display values
  const mapApiToDisplayValues = useCallback((apiData: any): FilterOptions => {
    // Handle project types
    const mappedProjectTypes = (apiData.project_types || []).map((type: Options) => ({
      text: PROJECT_TYPE_MAPPING[type.value] || type.text,
      value: type.value,
    }));

    // Handle profile types - API returns string[] of raw values
    const mappedProfileTypes = (apiData.profile_types as string[] || [])
      .filter((type: string) => !EXCLUDED_FILTER_PROFILE_TYPES.includes(type))
      .map((type: string) => ({
        text: PROFILE_TYPE_LABELS[type] || type,
        value: type,
      }));
    
    // Handle bucket types - always use static values, ignore backend
    const mappedBucketTypes = DEFAULT_BUCKET_TYPES;

    // Handle regions
    const mappedRegions = (apiData.regions || []).map((region: Options) => ({
      text: region.text,
      value: region.value,
    }));

    return {
      projectTypes: mappedProjectTypes,
      profileTypes: mappedProfileTypes,
      bucketTypes: mappedBucketTypes,
      regions: mappedRegions,
    };
  }, []);

  // Set default options on error
  const setDefaultOptions = useCallback(() => {
    setAvailableProjectTypes(DEFAULT_PROJECT_TYPES);
    setAvailableBucketTypes(DEFAULT_BUCKET_TYPES); // Always use static bucket types
    setAvailableProfileTypes(DEFAULT_PROFILE_TYPES);
    setAvailableRegions(DEFAULT_REGIONS);
  }, []);

  // Fetch filter options from API
  const fetchFilterOptions = useCallback(async (domainName?: string, region?: string) => {
    if (!domainName) {
      return;
    }
    
    setIsLoadingFilterOptions(true);
    
    try {
      const response = await getFilterOptions(msalInstance, {
        scope: "all",   
        domain_name: domainName,
        region_name: region || "all",
      });

      if (response.success && response.data) {
        const mappedOptions = mapApiToDisplayValues(response.data);
        
        setAvailableProfileTypes(mappedOptions.profileTypes);
        setAvailableProjectTypes(mappedOptions.projectTypes);
        // Always use static bucket types - don't update from API
        setAvailableBucketTypes(DEFAULT_BUCKET_TYPES);
        setAvailableRegions(mappedOptions.regions);
      } else {
        setDefaultOptions();
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
      setDefaultOptions();
    } finally {
      setIsLoadingFilterOptions(false);
    }
  }, [msalInstance, mapApiToDisplayValues, setDefaultOptions]);

  // Reset filter options to defaults
  const resetFilterOptions = useCallback(() => {
    setDefaultOptions();
  }, [setDefaultOptions]);

  return {
    availableProfileTypes,
    availableProjectTypes,
    availableBucketTypes, // This will always be the static DEFAULT_BUCKET_TYPES
    availableRegions,
    isLoadingFilterOptions,
    fetchFilterOptions,
    resetFilterOptions,
  };
};